// Fondo futurista global. Según la ruta:
//  - Landing (páginas públicas de marketing/contenido, excepto tienda):
//    mapa logístico vectorial + efectos (relieve satelital oscuro, rutas con
//    paquetes, hubs) como fondo de toda la página, con la cuadrícula Tron atenuada.
//  - Tienda online (/tienda, /vendedor, /carrito, /pagar): sin mapa ni logística,
//    solo la cuadrícula Tron.
//  - Plataforma (panel interno, rutas protegidas): SOLO las líneas de ruta
//    animadas (la "línea que viene hacia ti"), sin mapa ni efectos logísticos,
//    sobre la cuadrícula Tron.
// Compatible con temas (claro/oscuro/auto), skins y modo neón, y con el slider
// de transparencia. Se portaliza al <body> con z-index negativo.
import { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import LogisticsMapBackground from "@/components/LogisticsMapBackground";
// El mapa del panel (PlatformLogisticsMap) arrastra maplibre+leaflet (~950KB).
// Se carga bajo demanda: solo cuando el usuario enciende el mapa del panel.
// Así esos vendor chunks no entran al bundle inicial de la landing ni de login.
const PlatformLogisticsMap = lazy(() => import("@/components/PlatformLogisticsMap"));
// SatelliteLogisticsMap ya no es fondo global: vive solo en el hero del landing
// (como cabecera), no en toda la página. Importado en src/pages/Landing.jsx.

// Rutas de la tienda online (sin mapa).
const STORE = /^\/(tienda|vendedor|carrito|pagar)(\/|$)/;
// Rutas internas de la plataforma (panel logueado): solo líneas de ruta.
const PLATFORM = /^\/(panel|envios|pedidos|ordenes|reanudar-borrador|carga-masiva|carga-manual|reportes|analitica|rastreo|recolecciones|super-agente|asistente|asistente-rastreo|asistente-cuenta|billetera|perfil|soporte|libreta-direcciones|facturacion|referidos|notificaciones|integraciones|api-bhgc|verificacion-identidad|reclamaciones|correos|guia-configuracion|admin|facebook-insights|tenant-admin|cotizacion-rapida)(\/|$)/;

function routeCategory(pathname) {
  if (STORE.test(pathname)) return "store";
  if (PLATFORM.test(pathname)) return "platform";
  return "landing";
}

export default function TronBackground() {
  const { pathname } = useLocation();
  // Solo recalcula la categoría de ruta; el portal se re-renderiza únicamente
  // cuando cambia la categoría (landing/store/platform), no en cada pathname.
  const category = useMemo(() => routeCategory(pathname), [pathname]);
  const isStore = category === "store";
  const isPlatform = category === "platform";
  const isLanding = category === "landing";
  const [mapOn, setMapOn] = useState(() => {
    // Apagado por defecto: solo "on" (guardado por el switch) enciende el mapa.
    try { return localStorage.getItem("bhgc_plm_enabled_v2") === "on"; } catch { return false; }
  });
  useEffect(() => {
    const on = (e) => setMapOn(e.detail !== false);
    window.addEventListener("plm-enabled", on);
    return () => window.removeEventListener("plm-enabled", on);
  }, []);
  // Refleja el estado del mapa en <html> para que el CSS reaccione: el texto
  // que vive directamente sobre el mapa (.text-on-map) usa blanco solo cuando
  // el mapa está encendido en temas claros; apagado usa el color de texto del
  // tema (legible sobre el fondo claro). Centralizado aquí porque este
  // componente es global y ya sincroniza el evento plm-enabled.
  useEffect(() => {
    try { document.documentElement.setAttribute("data-plm-on", mapOn ? "on" : "off"); } catch { /* ignore */ }
  }, [mapOn]);
  // Memoiza el contenido del portal: solo cambia cuando cambia la categoría o
  // el estado del mapa, evitando re-renders en cada navegación dentro de la
  // misma categoría.
  const content = useMemo(() => (
    <div className={`tron-bg${isLanding ? " tron-bg--landing" : ""}`} aria-hidden="true">
      {isPlatform && mapOn && (
        <Suspense fallback={null}>
          <PlatformLogisticsMap />
        </Suspense>
      )}
      {!isPlatform && <div className="tron-grid" />}
      {!isPlatform && <div className="tron-floor" />}
      {!isPlatform && <div className="tron-glow" />}
      {isLanding && <div className="tron-fusion" />}
    </div>
  ), [isPlatform, isLanding, isStore, mapOn]);
  return createPortal(content, document.body);
}